import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, Link, Stack, Typography, useTheme } from "@mui/material";
import { alpha, darken } from "@mui/material/styles";

import AccordionComponent from "../components/Accordion";
import ApplicationSection from "../components/ApplicationSection";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import UseFetchApplicationsWithQuestionnaires from "../hooks/useFetchApplicationsWithQuestionnaires";
import { routes } from "../routes/routes";
import { BANNER_COLOR_LIGHT } from "./root";

const BOGOTA_TIME_ZONE = "America/Bogota";

const getDateKeyInBogota = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: BOGOTA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const dateParts = Object.fromEntries(parts.filter(({ type }) => type !== "literal").map(({ type, value }) => [type, value]));

  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
};

const isValidDateKey = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const [year, month, day] = value.split("-").map(Number);

  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  return parsedDate.getUTCFullYear() === year && parsedDate.getUTCMonth() === month - 1 && parsedDate.getUTCDate() === day;
};

const normalizeApplicationDate = (value) => {
  if (!value) {
    return null;
  }

  const stringValue = String(value).trim();

  if (isValidDateKey(stringValue)) {
    return stringValue;
  }

  const parsedDate = new Date(stringValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return getDateKeyInBogota(parsedDate);
};

const getBogotaToday = () => {
  return getDateKeyInBogota(new Date());
};

const isApplicationWithinDate = (application, bogotaToday) => {
  const dateMin = normalizeApplicationDate(application.date_min);

  const dateMax = normalizeApplicationDate(application.date_max);

  if (!dateMin || !dateMax) {
    return false;
  }

  if (dateMin > dateMax) {
    return false;
  }

  return bogotaToday >= dateMin && bogotaToday <= dateMax;
};

const formatApplicationDate = (value) => {
  const normalizedDate = normalizeApplicationDate(value);

  if (!normalizedDate) {
    return "Sin fecha";
  }

  const [year, month, day] = normalizedDate.split("-").map(Number);

  const date = new Date(Date.UTC(year, month - 1, day, 12));

  return new Intl.DateTimeFormat("es-CO", {
    timeZone: BOGOTA_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const useBogotaToday = () => {
  const [bogotaToday, setBogotaToday] = useState(getBogotaToday);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const currentBogotaDate = getBogotaToday();

      setBogotaToday((previousDate) => {
        return previousDate === currentBogotaDate ? previousDate : currentBogotaDate;
      });
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return bogotaToday;
};

const ApplicationsDetailsPage = () => {
  const theme = useTheme();
  const bogotaToday = useBogotaToday();

  const { data: applications, error, loading, refetch } = UseFetchApplicationsWithQuestionnaires();

  const [errorOpen, setErrorOpen] = useState(false);
  const [expandedAppId, setExpandedAppId] = useState(null);

  useEffect(() => {
    if (error) {
      setErrorOpen(true);
    }
  }, [error]);

  const accordionInfo = useMemo(() => {
    if (!applications?.length) {
      return [];
    }

    const sortedApplications = applications
      .map((application) => ({
        application,
        dateMin: normalizeApplicationDate(application.date_min),
        isWithinDate: isApplicationWithinDate(application, bogotaToday),
        isEnabled: application.enabled !== false,
      }))
      .sort((itemA, itemB) => {
        const getPriority = (item) => {
          // Aplicaciones habilitadas y dentro de fecha
          if (item.isEnabled && item.isWithinDate) {
            return 0;
          }

          // Aplicaciones deshabilitadas
          if (!item.isEnabled) {
            return 1;
          }

          // Aplicaciones habilitadas, pero fuera de fecha
          return 2;
        };

        const priorityA = getPriority(itemA);
        const priorityB = getPriority(itemB);

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        if (!itemA.dateMin && !itemB.dateMin) {
          return (itemA.application.name ?? "").localeCompare(itemB.application.name ?? "", "es");
        }

        if (!itemA.dateMin) {
          return 1;
        }

        if (!itemB.dateMin) {
          return -1;
        }

        const dateComparison = itemA.dateMin.localeCompare(itemB.dateMin);

        if (dateComparison !== 0) {
          return dateComparison;
        }

        return (itemA.application.name ?? "").localeCompare(itemB.application.name ?? "", "es");
      });

    return sortedApplications.map(({ application, isWithinDate, isEnabled }) => {
      return {
        key: application.id,

        title: (
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              pr: 1,
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  overflowWrap: "anywhere",
                }}
              >
                {application.name}
              </Typography>

              <Typography
                variant="caption"
                component="div"
                sx={(muiTheme) => ({
                  color: isEnabled && isWithinDate ? muiTheme.palette.text.secondary : muiTheme.palette.grey[700],
                })}
              >
                {formatApplicationDate(application.date_min)}
                {" — "}
                {formatApplicationDate(application.date_max)}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              {!isEnabled ? (
                <Chip
                  size="small"
                  label="Deshabilitada"
                  variant="outlined"
                  sx={(muiTheme) => ({
                    backgroundColor: muiTheme.palette.grey[300],
                    color: muiTheme.palette.grey[900],
                    borderColor: muiTheme.palette.grey[500],
                  })}
                />
              ) : (
                <Chip
                  size="small"
                  label={isWithinDate ? "Dentro de fecha" : "Fuera de fecha"}
                  variant="outlined"
                  sx={(muiTheme) => ({
                    fontWeight: 500,
                    backgroundColor: isWithinDate ? BANNER_COLOR_LIGHT : muiTheme.palette.grey[100],
                    color: isWithinDate ? muiTheme.palette.text.primary : muiTheme.palette.grey[700],
                    borderColor: isWithinDate ? darken(BANNER_COLOR_LIGHT, 0.25) : muiTheme.palette.grey[400],
                  })}
                />
              )}
            </Stack>
          </Box>
        ),

        accordionSx: (muiTheme) => {
          return {
            border: `1px solid ${muiTheme.palette.divider}`,
            boxShadow: "none",

            "&::before": {
              display: "none",
            },
          };
        },

        summarySx: (muiTheme) => {
          // Solo se considera activa visualmente cuando está
          // habilitada y dentro del rango de fechas.
          const isActive = isEnabled && isWithinDate;

          return {
            backgroundColor: isActive ? BANNER_COLOR_LIGHT : muiTheme.palette.grey[100],

            color: muiTheme.palette.text.primary,

            "&:hover": {
              backgroundColor: isActive ? BANNER_COLOR_LIGHT : muiTheme.palette.grey[200],
            },

            "&.Mui-expanded": {
              backgroundColor: isActive ? BANNER_COLOR_LIGHT : muiTheme.palette.grey[100],
            },

            "& .MuiAccordionSummary-content": {
              minWidth: 0,
            },

            "& .MuiSvgIcon-root": {
              color: isActive ? muiTheme.palette.text.secondary : muiTheme.palette.grey[600],
            },
          };
        },

        details: (
          <Box
            sx={{
              pl: { xs: 2, md: 4 },
              py: 1,
            }}
          >
            <ApplicationSection app={application} isExpanded={expandedAppId === application.id} refetchApplications={refetch} />
          </Box>
        ),
      };
    });
  }, [applications, bogotaToday, expandedAppId, refetch]);

  return (
    <>
      <Box
        sx={{
          minWidth: "100%",
          p: { xs: 2, md: 5 },
          overflowX: "auto",
        }}
      >
        <BreadcrumbsComponent
          breadcrumbs={[
            <Link underline="none" key="home" color="inherit" href={routes.root}>
              Página Principal
            </Link>,

            <Typography key="applications" color="secondary" sx={{ fontSize: "14px" }}>
              Aplicaciones
            </Typography>,
          ]}
        />

        <Box
          sx={{
            mt: 4,
            maxWidth: 1200,
            mx: "auto",
            p: { xs: 2, md: 4 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: theme.shadows[1],
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Aplicaciones
          </Typography>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip
              size="small"
              label="Dentro de fecha"
              variant="outlined"
              sx={(muiTheme) => ({
                backgroundColor: BANNER_COLOR_LIGHT,
                color: muiTheme.palette.text.primary,
                borderColor: darken(BANNER_COLOR_LIGHT, 0.25),
                fontWeight: 500,
              })}
            />

            <Chip
              size="small"
              label="Fuera de fecha"
              variant="outlined"
              sx={(muiTheme) => ({
                backgroundColor: muiTheme.palette.grey[100],
                color: muiTheme.palette.grey[700],
                borderColor: muiTheme.palette.grey[400],
                fontWeight: 500,
              })}
            />

            <Chip
              size="small"
              label="Deshabilitada"
              variant="outlined"
              sx={(muiTheme) => ({
                backgroundColor: muiTheme.palette.grey[300],
                color: muiTheme.palette.grey[900],
                borderColor: muiTheme.palette.grey[500],
              })}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
            Fecha actual: {formatApplicationDate(bogotaToday)}
          </Typography>

          <Box
            sx={{
              "& .MuiAccordion-root": {
                mt: 1.5,
                borderRadius: "4px",
                overflow: "hidden",
              },
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress />
              </Box>
            ) : accordionInfo.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                No hay aplicaciones disponibles
              </Typography>
            ) : (
              <AccordionComponent accordionInfo={accordionInfo} expandedAppId={expandedAppId} onChange={setExpandedAppId} />
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <ErrorSnackbar
          open={errorOpen}
          onClose={() => setErrorOpen(false)}
          title="Ha ocurrido un error"
          message={error.message || "No fue posible obtener las aplicaciones."}
        />
      )}
    </>
  );
};

export default ApplicationsDetailsPage;
